/**
 * Compact developer snapshot for Inspector Files / Diff / Evidence.
 * Not an editor: lists what Brunel already recorded.
 */
import type { AutomaticHandoff, ComposedTest, HandoffReference } from '../handoff/compose.js';

export interface ChangesSnapshot {
  readonly workerLabel: string | null;
  readonly projectName: string | null;
  readonly taskTitle: string | null;
  readonly taskId: string | null;
  readonly nodeName: string | null;
  readonly sessionId: string | null;
  readonly branch: string | null;
  readonly commitSha: string | null;
  readonly pullRequest: { readonly number: number | null; readonly url: string | null } | null;
  readonly filesChanged: readonly string[] | null;
  readonly tests: readonly ComposedTest[] | null;
  readonly artifacts: readonly HandoffReference[];
  readonly patchText: string | null;
  readonly runSummary: string | null;
}

export function changesSnapshotFromSelection(input: {
  readonly workerLabel: string | null;
  readonly projectName: string | null;
  readonly taskTitle: string | null;
  readonly taskId: string | null;
  readonly nodeName: string | null;
  readonly sessionId: string | null;
  readonly branch?: string | null;
  readonly filesChanged?: readonly string[] | null;
  readonly patchText?: string | null;
  readonly runSummary?: string | null;
  readonly handoff: AutomaticHandoff | null;
}): ChangesSnapshot {
  const handoff = input.handoff;
  const prRef = handoff?.codeReferences.find((item) => item.kind === 'pull_request') ?? null;
  const commitRef = handoff?.codeReferences.find((item) => item.kind === 'commit') ?? null;
  const artifacts = (handoff?.codeReferences ?? []).filter((item) => item.kind === 'artifact');
  const prNumber = prRef?.reference && /^#?\d+$/.test(prRef.reference)
    ? Number(prRef.reference.replace('#', ''))
    : null;
  return {
    workerLabel: input.workerLabel ?? handoff?.worker?.label ?? null,
    projectName: input.projectName ?? handoff?.project ?? null,
    taskTitle: input.taskTitle ?? handoff?.task?.title ?? null,
    taskId: input.taskId ?? handoff?.task?.id ?? null,
    nodeName: input.nodeName,
    sessionId: input.sessionId,
    branch: input.branch ?? null,
    commitSha: commitRef?.reference ?? null,
    pullRequest: prRef == null ? null : { number: Number.isFinite(prNumber) ? prNumber : null, url: prRef.url },
    filesChanged: input.filesChanged ?? handoff?.filesChanged ?? null,
    tests: handoff?.tests ?? null,
    artifacts,
    patchText: input.patchText ?? null,
    runSummary: input.runSummary ?? handoff?.result ?? null,
  };
}

export function parseFilesChanged(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const out: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (trimmed === '') continue;
    out.push(trimmed.slice(0, 240));
    if (out.length >= 200) break;
  }
  return out;
}
